/**
 * The GLOBAL header readout for the A/D hotkey target.
 *
 * One compact line beside the one-click arm control that answers, at a
 * glance, "where would A/D land right now?" — the last EXPLICITLY AIMED card
 * in human terms, a truthful state, and the disengage control:
 *
 *   armed, no target   →  "A/D — aim a game"           (teaches the chip)
 *   …opted into Pull All →  "A/D · Spacebar — aim a game"  (#802: the pull
 *                            key needs the SAME gesture, and nothing said so)
 *   both actionable    →  "⌨ NYJ ML · Ready A/D  ×"
 *   …S + Shift+S ready →  "⌨ NYJ ML · Ready A/D · S · ⇧S 3×=300  ×"  (#1067:
 *                            the multiplier and the per-side quantity it sends)
 *   one actionable     →  "⌨ NYJ ML · Ready A  ×"  (or Ready D)
 *   neither            →  "⌨ NYJ ML · Unavailable  ×"
 *   Paused (expanded)  →  "⏸ NYJ ML · Paused: expanded  ×"  (takes precedence)
 *   resolver gone      →  "A/D target unavailable  ×"
 *
 * "Actionable" is derived from the SAME resolved targets a keypress would
 * fire through: the side exists, displays a price, and its line is active.
 * A readout that said "Ready" over two null prices would be teaching the
 * trader to trust a dead target — presentation-only, but never untruthful.
 *
 * SINGLE SOURCE OF TRUTH: everything renders from the existing
 * `lt2FocusStore` — `focusedCardId` + `resolveFocused()` (the same resolver
 * the keypress fires through). There is no second target state anywhere:
 * `presentationSeq` is only an invalidation signal the FOCUSED card bumps on
 * its own re-renders, so label/paused changes appear without polling. The
 * readout renders nothing while disarmed or without the hotkey entitlement —
 * disarming already clears the focus (the listener's subscription), so an
 * idle page shows an idle header. GameDetail's header uses
 * `Lt2TargetReadoutSlot` instead (#1070), which holds an EMPTY box of the
 * readout's fixed size in those states, so arming fills a box already there.
 *
 * The ✕ reuses `clearFocus` (the badge's own action) and carries
 * `data-lt2-hotkey-control` + a self-blur, so pressing A right after
 * disengaging is a silent no-op rather than an "owned control" rejection.
 */
import { useDisplaySettings } from '@/stores/displaySettings';
import { pillPriceCents } from '@/views/ValueDashboard/ladder/helpers';
import {
  PASSIVE_PRICE_DISCLOSURE,
  hotkeyLabelFor,
  hotkeyPairLabel,
  isActionActive,
  isActionUnbound,
  shiftTwoSidedChordLabel,
} from '@/lib/trading/liveHotkeyRegistry';
import {
  useLiveHotkeyGrants,
  useLiveHotkeyMap,
  useLiveKeyList,
  useTwoSidedMultiplier,
} from '@/hooks/useLiveHotkeys';
import { useAuthStore } from '@/stores/authStore';
import { useLt2FocusStore, type Lt2HotkeyTarget } from '@/stores/lt2FocusStore';
import { twoSidedReadiness } from '@/lib/trading/twoSidedReadiness';
import { CHECK_OPEN_ORDERS, oneClickCeiling, twoSidedHoldBlocks, useOneClickStore } from '@/stores/oneClickStore';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { cn } from '@/lib/infra/utils';
import { Tooltip } from '@/components/atoms/Tooltip';
import { HelpPopover } from '@/components/molecules/HelpPopover';
import { LT2_READOUT_LINE_CLASS, LT2_READOUT_QUICK_REF, useReadoutBox } from '@/lib/trading/lt2Readout';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Taste D7 — the readout's separator. A line may break BEFORE the dot, never
 * after it, so no line ends on a stranded "·".
 */
const SEP = ' ·\u00a0';

/**
 * `fixed` (#1070, GameDetail's header): every state renders in one fixed
 * 240px × 3-line box. Without it the readout keeps its wrapping width (the
 * Value Dashboard's toolbar cell, which must shrink rather than reserve).
 */
export function Lt2TargetReadout({ fixed = false }: { fixed?: boolean } = {}) {
  const lt2Access = useLt2Access();
  const hotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const armed = useOneClickStore((s) => s.armed);
  // #724 — an earlier S press with a lost leg refuses S until it is seen or
  // the trader disarms; the header must not advertise S meanwhile.
  const twoSidedHold = useOneClickStore((s) => s.twoSidedHold);
  // #802 — the pull key is named here only while the trader has opted in.
  // Off, Space is an ordinary browser key and naming it would advertise a
  // capability this trader does not have.
  const pullAllEnabled = useDisplaySettings((s) => s.lt2PullAllEnabled);
  // D15 — keys the relay refused for this desk this session: never offered.
  const deskTwoSidedOff = useOneClickStore((s) => s.deskOff.twoSided);
  const deskPullAllOff = useOneClickStore((s) => s.deskOff.pullAll);
  // #725 — every key this readout names is the signed-in trader's own.
  const hotkeys = useLiveHotkeyMap();
  const grants = useLiveHotkeyGrants();
  const focusedCardId = useLt2FocusStore((s) => s.focusedCardId);
  // #1067 — what Shift+S would send: the multiplier, the base it multiplies,
  // and the ceiling the store will hold the product to.
  const multiplier = useTwoSidedMultiplier();
  const sizeContracts = useOneClickStore((s) => s.sizeContracts);
  const oneClickMax = useAuthStore((s) => s.user?.oneClickMaxContracts ?? null);
  // Subscribe to the focused card's render announcements so Ready/Paused and
  // the label re-derive exactly when they may have changed.
  const presentationSeq = useLt2FocusStore((s) => s.presentationSeq);
  // D20 — whether the aimed card's price rows are on screen (null: unknown).
  const onScreen = useAimedPriceOnScreen(armed ? focusedCardId : null, presentationSeq);

  if (!lt2Access || !hotkeysEnabled || !armed) return null;

  const pair = hotkeyPairLabel(hotkeys, 'passive-top', 'passive-bottom', grants);
  const topKey = hotkeyLabelFor(hotkeys, 'passive-top', grants);
  const bottomKey = hotkeyLabelFor(hotkeys, 'passive-bottom', grants);
  // #724 — S is named only while it FIRES: opted in, holding a key, and the
  // focused card passing the same `twoSidedReadiness` the key handler runs.
  // It needs both sides ready, so it is shown beside "Ready A/D" and nowhere
  // else; otherwise the tooltip says why S would refuse.
  const twoSidedKey =
    grants.twoSided && !deskTwoSidedOff && !isActionUnbound(hotkeys, 'two-sided', grants)
      ? hotkeyLabelFor(hotkeys, 'two-sided', grants)
      : null;

  const targets =
    focusedCardId != null ? useLt2FocusStore.getState().resolveFocused() : null;

  if (focusedCardId == null) {
    // #802 — the unaimed line named A/D only, so an opted-in trader had
    // nothing on screen telling them Pull All needs the same gesture. Asked
    // through `isActionUnbound` rather than by comparing the label to an em
    // dash: a trader who remapped the pull away from Space holds no pull key,
    // and a readout must never name one that fires nothing.
    const namesPull =
      pullAllEnabled && !deskPullAllOff && !isActionUnbound(hotkeys, 'pull-all', grants);
    const pullOffNote =
      pullAllEnabled && deskPullAllOff
        ? ` ${hotkeyLabelFor(hotkeys, 'pull-all', grants)} Pull All is off on this desk.`
        : '';
    const keys = namesPull
      ? `${pair}${SEP}${hotkeyLabelFor(hotkeys, 'pull-all', grants)}`
      : pair;
    return (
      <ReadoutFrame
        fixed={fixed}
        help
        tone="text-muted-foreground"
        title={
          namesPull
            ? `${pair} and ${hotkeyLabelFor(hotkeys, 'pull-all', grants)} are armed but not aimed. Click a moneyline game's ⌨ chip, or a spread/total row, to aim them. Pull All cancels on the aimed game only.`
            : `${pair} hotkeys are armed but not aimed. Click a moneyline game's ⌨ chip, or a spread/total row, to aim them.${pullOffNote}`
        }
      >
        {keys} — aim a game
      </ReadoutFrame>
    );
  }

  const clear = (
    <button
      type="button"
      data-lt2-hotkey-control=""
      onClick={(e) => {
        e.currentTarget.blur();
        useLt2FocusStore.getState().clearFocus();
      }}
      className="text-muted-foreground hover:text-foreground rounded leading-tight"
      aria-label="Disengage hotkey target"
      title={`Disengage — ${pair} stop targeting this card. Esc does the same.`}
    >
      ×
    </button>
  );

  if (!targets) {
    // Focused id with no live resolver: reachable only inside a teardown
    // commit (register()'s cleanup clears synchronously) or when a resolver
    // reports its market gone. Truthful, never pretend-Ready.
    return (
      <ReadoutFrame
        fixed={fixed}
        help
        tone="text-amber-400"
        clear={clear}
        title={`The aimed card is no longer resolvable — the next ${pair} press clears it.`}
      >
        {pair} target unavailable
      </ReadoutFrame>
    );
  }

  // A side is actionable exactly when the keypress guard would let it fire:
  // it exists, shows a price, and its line is active. D20 — and the price is
  // a whole cent (the submit guard refuses 54.5¢), and the row showing it is
  // on screen (the fence refuses a scrolled-away price).
  const priced = (t: Lt2HotkeyTarget | null): t is Lt2HotkeyTarget =>
    t != null && t.priceCents != null && t.availability === 'active';
  const wholeCent = (t: Lt2HotkeyTarget | null): boolean =>
    priced(t) && Number.isInteger(t.priceCents);
  const shown = (slot: 'a' | 'd'): boolean => onScreen == null || onScreen[slot];
  const aReady = wholeCent(targets.a) && shown('a');
  const dReady = wholeCent(targets.d) && shown('d');
  const subCent = [targets.a, targets.d].find(
    (t): t is Lt2HotkeyTarget => priced(t) && !Number.isInteger(t.priceCents),
  );
  const offScreen = (wholeCent(targets.a) && !shown('a')) || (wholeCent(targets.d) && !shown('d'));
  const twoSidedTargets = twoSidedKey != null ? twoSidedReadiness(targets, twoSidedKey) : null;
  const twoSided =
    twoSidedTargets?.ok === true && twoSidedHoldBlocks(twoSidedHold)
      ? {
          ok: false as const,
          legLabel: null,
          // Taste D7 — the held press by its own label (S, 2, 3 or ⇧S).
          reason: `earlier ${twoSidedHold?.label ?? twoSidedKey} has a side not yet confirmed — ${CHECK_OPEN_ORDERS}, then "I checked, release ${twoSidedKey}" on the status line, or turn 1-Click off`,
        }
      : twoSidedTargets;
  const twoSidedReady = twoSided?.ok === true;
  // D18 — 2 and 3 post both sides one and two levels down: the same checks
  // and hold as S, so they are named wherever S is, by the trader's keys.
  const levelDownKeys = (['two-sided-down-1', 'two-sided-down-2'] as const)
    .filter((id) => !isActionUnbound(hotkeys, id, grants) && isActionActive(id, grants))
    .map((id) => hotkeyLabelFor(hotkeys, id, grants));
  // #1067 — Shift+S is S made larger, so it can only be ready where S is.
  // Named with its multiplier and per-side quantity when it would fire;
  // otherwise the tooltip says why, in the store's own terms.
  const chord = twoSidedKey != null ? shiftTwoSidedChordLabel(hotkeys, grants) : null;
  const ceiling = oneClickCeiling({ oneClickMaxContracts: oneClickMax });
  const shift = ((): { ok: true; n: number; perSide: number } | { ok: false; reason: string } | null => {
    if (chord == null) return null;
    if (!multiplier.ok) return multiplier;
    const n = multiplier.multiplier;
    const perSide = sizeContracts * n;
    return perSide > ceiling
      ? { ok: false, reason: `${n}× of ${sizeContracts} = ${perSide} per side exceeds your ${ceiling} one-click limit` }
      : { ok: true, n, perSide };
  })();
  const shiftReady = twoSidedReady && shift?.ok === true;
  // Non-breaking: when the line wraps, the chord and the quantity it sends
  // must stay one token ("⇧S 3×=300"), never "⇧S" stranded at a line end.
  const shiftLabel = shift?.ok === true ? `${SEP}${chord}\u00a0${shift.n}×=${shift.perSide}` : '';
  const shiftTitle =
    shift == null || !twoSidedReady
      ? ''
      : shift.ok
        ? ` ${chord} posts the same two orders at ${shift.n}× that size: ${shift.perSide} per side.`
        : ` ${chord} would refuse here: ${shift.reason}.`;
  const paused = targets.pausedReason === 'expanded';
  // #725 — the readiness DISCRIMINANT is kept separate from the rendered
  // string. The old code recovered the key with `state.slice(-1)`, which is
  // only correct while the keys are single letters called A and D; a remap to
  // "Space" would have made the tooltip name the letter "e".
  const readiness: 'paused' | 'both' | 'top' | 'bottom' | 'offscreen' | 'subcent' | 'none' = paused
    ? 'paused'
    : aReady && dReady
      ? 'both'
      : aReady
        ? 'top'
        : dReady
          ? 'bottom'
          : offScreen
            ? 'offscreen'
            : subCent != null
              ? 'subcent'
              : 'none';
  // D17 — what a press at the shown price does, for a side that is not
  // post-only (the moneyline pills): it takes when the price reaches the ask,
  // joins when it is the bid (Maker), and otherwise rests at the last price.
  const modeA = priceAction(targets.a);
  const modeD = priceAction(targets.d);
  const mode =
    readiness === 'both'
      ? modeA === 'takes ask' || modeD === 'takes ask'
        ? 'takes ask'
        : modeA === modeD
          ? modeA
          : null
      : readiness === 'top'
        ? modeA
        : readiness === 'bottom'
          ? modeD
          : null;
  const modeLabel = mode != null ? `${SEP}${mode}` : '';
  const state =
    readiness === 'paused'
      ? 'Paused: expanded'
      : readiness === 'both'
        ? `Ready ${pair}${modeLabel}${twoSidedReady ? `${SEP}${[twoSidedKey, ...levelDownKeys].join(SEP)}` : ''}${shiftReady ? shiftLabel : ''}`
        : readiness === 'top'
          ? `Ready ${topKey}${modeLabel}`
          : readiness === 'bottom'
            ? `Ready ${bottomKey}${modeLabel}`
            : readiness === 'offscreen'
              ? 'Off screen — scroll back'
              : readiness === 'subcent'
                ? 'Not ready: sub-cent price'
                : 'Unavailable';
  const degraded =
    readiness === 'paused' || readiness === 'none' || readiness === 'offscreen' || readiness === 'subcent';
  // D15 — the trader opted into S, but the relay turned it off on this desk.
  const deskOffNote =
    grants.twoSided && deskTwoSidedOff
      ? ` ${hotkeyLabelFor(hotkeys, 'two-sided', grants)} is off on this desk.`
      : '';
  const title =
    (readiness === 'paused'
      ? `${pair} are aimed at ${targets.cardLabel} but paused while its card is expanded — collapse the row to resume.`
      : readiness === 'offscreen'
        ? `${pair} are aimed at ${targets.cardLabel}, but its price is scrolled off screen. ${pair} fire only while the aimed price is on screen.`
      : readiness === 'subcent'
        ? `Whole-cent prices only for hotkeys/one-click (${subCent?.priceCents}¢); use the ticket.`
      : readiness === 'none'
        ? `${pair} are aimed at ${targets.cardLabel}, but neither side shows an active priced line right now — a press would be refused truthfully.`
        : readiness === 'both'
          ? `${pair} are aimed at ${targets.cardLabel} at the visible one-click size.${
              twoSided == null
                ? ''
                : twoSided.ok
                  ? ` ${twoSidedKey} posts both sides at once: two orders, each at that size.${
                      targets.threeWay === true
                        ? ' On this three-way game (a soccer 1X2) S posts NO on both teams, and both team NOs pay on a draw: if both fill you are positioned on the draw, not hedged.'
                        : ''
                    }${shiftTitle}`
                  : ` ${twoSidedKey} would refuse here: ${twoSided.reason}.`
            }`
          : `Only ${readiness === 'top' ? topKey : bottomKey} has an active priced line on ${targets.cardLabel}; the other side would be refused truthfully.${
              offScreen ? ` ${pair} fire only while the aimed price is on screen.` : ''
            }${subCent != null ? ` Whole-cent prices only for hotkeys/one-click (${subCent.priceCents}¢); use the ticket.` : ''}`) +
    deskOffNote +
    (mode != null ? ` ${pair}: ${PASSIVE_PRICE_DISCLOSURE}` : '');
  return (
    <ReadoutFrame fixed={fixed} help tone={degraded ? 'text-amber-400' : 'text-primary'} clear={clear} title={title}>
      {paused ? '⏸' : '⌨'} {targets.cardLabel}
      {SEP}
      {state}
    </ReadoutFrame>
  );
}

/**
 * D17 — what a press at this target's shown price does, or null when it is
 * post-only (it can only rest) or the book cannot say. Read from the same
 * book the card paints: at or through the ask it takes.
 */
function priceAction(t: Lt2HotkeyTarget | null): 'takes ask' | 'joins bid' | 'last' | null {
  if (t == null || t.postOnly || t.priceCents == null) return null;
  const ask = pillPriceCents(t.book, t.side, 'taker');
  if (ask != null && t.priceCents >= ask) return 'takes ask';
  if (t.joinsBid) return 'joins bid';
  return t.book != null ? 'last' : null;
}

/** D20 — the share of a ROW-shaped price mark that must show, as the
 *  listener's fence counts it (`MIN_VISIBLE_FRACTION`); an EXACT mark
 *  (`data-lt2-exact`) must show whole. */
const ROW_VISIBLE_RATIO = 0.5;
const EXACT_VISIBLE_RATIO = 0.99;

/**
 * D20 — is the aimed card's displayed price on screen, per side? Watches the
 * card's `[data-lt2-price]` marks with an IntersectionObserver (never a
 * scroll listener), so it costs nothing until a mark crosses the line. The
 * marks are re-queried when the focused card re-renders (`seq`), and the
 * observer is rebuilt only when the set of marks changed. Null: unknown (no
 * observer, nothing aimed, or no marks painted) — the readout then claims
 * nothing about the screen; the keypress fence stays the authority.
 */
function useAimedPriceOnScreen(cardId: string | null, seq: number): { a: boolean; d: boolean } | null {
  const [onScreen, setOnScreen] = useState<{ cardId: string; a: boolean; d: boolean } | null>(null);
  const watched = useRef<{ cardId: string; rows: Element[]; observer: IntersectionObserver } | null>(null);
  useEffect(() => {
    if (cardId == null || typeof IntersectionObserver === 'undefined') {
      watched.current?.observer.disconnect();
      watched.current = null;
      return;
    }
    const rows = [...document.querySelectorAll('[data-lt2-focused][data-lt2-price]')].filter(
      (r) => r.getAttribute('data-lt2-focused') === cardId,
    );
    const current = watched.current;
    if (
      current != null &&
      current.cardId === cardId &&
      current.rows.length === rows.length &&
      current.rows.every((r, i) => r === rows[i])
    ) {
      return;
    }
    current?.observer.disconnect();
    watched.current = null;
    // No marks painted: nothing to watch. The aim clears when its card
    // unregisters, and an expanded card's suppressed marks read as Paused
    // first, so the last answer for this card is not left standing.
    if (rows.length === 0) return;
    const visible = new Map<Element, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const exact = entry.target.hasAttribute('data-lt2-exact');
          visible.set(
            entry.target,
            entry.isIntersecting &&
              (exact
                ? entry.intersectionRatio >= EXACT_VISIBLE_RATIO
                : entry.intersectionRatio > ROW_VISIBLE_RATIO),
          );
        }
        const slotShown = (slot: 'a' | 'd') =>
          rows.some((r) => {
            const mark = r.getAttribute('data-lt2-price');
            return (mark === '' || mark === slot) && visible.get(r) === true;
          });
        setOnScreen({ cardId, a: slotShown('a'), d: slotShown('d') });
      },
      { threshold: [0, ROW_VISIBLE_RATIO, 0.51, EXACT_VISIBLE_RATIO, 1] },
    );
    rows.forEach((r) => observer.observe(r));
    watched.current = { cardId, rows, observer };
  }, [cardId, seq]);
  // Disconnect when the readout goes away.
  useEffect(
    () => () => {
      watched.current?.observer.disconnect();
      watched.current = null;
    },
    [],
  );
  return onScreen != null && onScreen.cardId === cardId ? { a: onScreen.a, d: onScreen.d } : null;
}

/**
 * Header variant that keeps the readout's place while one-click is disarmed
 * (#1070): with hotkeys on, an empty frame of the same size stands in, so
 * arming fills a box that was already there instead of inserting one.
 */
export function Lt2TargetReadoutSlot() {
  // Layout follows the grant slot (a restored grant keeps the frame while it
  // confirms); the readout itself still requires the confirmed grant.
  const lt2Slot = useGrantSlot('lt2Access');
  const armed = useOneClickStore((s) => s.armed);
  if (!useLt2ReadoutSlotShown()) return null;
  return lt2Slot === 'ready' && armed ? <Lt2TargetReadout fixed /> : <Lt2TargetReadoutFrame />;
}

/** Whether {@link Lt2TargetReadoutSlot} takes up space (its frame or the
 *  readout), so a host can hold that space when it stops rendering it. */
export function useLt2ReadoutSlotShown(): boolean {
  const lt2Slot = useGrantSlot('lt2Access');
  const hotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  return lt2Slot !== 'hidden' && hotkeysEnabled;
}

/** The readout's empty fixed frame. D5 — a host whose access was taken away
 *  keeps it while the notice shows, so nothing around it reflows. */
export function Lt2TargetReadoutFrame() {
  return <ReadoutFrame fixed tone="text-muted-foreground" />;
}

/**
 * The readout's box. The clear (×) leads in its own slot, so it never rides
 * the end of text whose length changes on every quote tick.
 *
 * `fixed` (#1070): 240px × three 11px lines, whatever it says — three lines
 * retain a stable header. Longer names plus remapped keys and the Shift+S
 * quantity scroll with a visible cue, rather than hiding the quantity.
 * Unfixed, it is
 * the bounded wrapping span the Value Dashboard's toolbar cell needs.
 */
function ReadoutFrame({
  fixed,
  tone,
  clear,
  title,
  children,
  help = false,
}: {
  fixed: boolean;
  tone: string;
  clear?: ReactNode;
  title?: string;
  children?: ReactNode;
  /** Section 4 — the key list trigger in the trailing slot. */
  help?: boolean;
}) {
  return (
    <span
      data-testid="lt2-target-readout"
      className={cn(
        'flex items-start gap-1 font-mono text-[11px] leading-tight',
        fixed ? 'h-[calc(3*1.25*11px)] w-[240px] shrink-0' : 'min-w-0 max-w-[240px]',
        tone,
      )}
    >
      <span className="flex w-3 shrink-0 justify-center">{clear}</span>
      {fixed ? <FixedReadoutText title={title}>{children}</FixedReadoutText> : (
        <span data-testid="lt2-target-readout-text" title={title} className="min-w-0 break-words">
          {children}
        </span>
      )}
      {/* Its own fixed slot, held in every state, so the trigger never rides
          the end of text that changes on every tick. */}
      <span className="flex w-3.5 shrink-0 justify-center">{help && <LiveKeyListHelp />}</span>
    </span>
  );
}

/**
 * Section 4 — the trader's live keys, one click away (HelpPopover, #602).
 * The trigger owns its keys while focused (data-lt2-owner, from HelpPopover),
 * so asking for help can never place an order; when the list closes the
 * trigger lets go of focus, so the next A acts on the aimed card.
 */
function LiveKeyListHelp() {
  const keys = useLiveKeyList();
  const trigger = (wrapper: HTMLElement): HTMLButtonElement | null => wrapper.querySelector('button');
  return (
    <span
      className="inline-flex"
      // A click on the open trigger closes the list: let go of focus as it
      // does. Escape closes it and hands focus back to the trigger (#602); by
      // the key's release the list is closed, so let go then.
      onClickCapture={(e) => {
        const button = trigger(e.currentTarget);
        if (button?.getAttribute('aria-expanded') === 'true') button.blur();
      }}
      onKeyUpCapture={(e) => {
        const button = trigger(e.currentTarget);
        if (e.key === 'Escape' && button != null && button.getAttribute('aria-expanded') !== 'true') button.blur();
      }}
    >
      <HelpPopover
        label="Live trading keys"
        title="Live trading keys"
        trigger="?"
        triggerClassName="text-muted-foreground hover:text-foreground rounded font-sans leading-tight"
        panelWidth={280}
      >
        {keys}
      </HelpPopover>
    </span>
  );
}

/** The readout's visible text: what "the box now says something else" compares. */
function textOf(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  return '';
}

/** A fixed header must not hide an active key or its effective quantity. */
function FixedReadoutText({ title, children }: { title?: string; children?: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [hiddenBelow, setHiddenBelow] = useState(false);
  // Focusable while it overflows or while it holds focus (#1080). A line the
  // hotkey listener shows here belongs to the text it was shown over.
  const readout = useReadoutBox(ref, textOf(children));
  const focusable = overflowing || readout.holdsFocus;
  const measure = useCallback(() => {
    const box = ref.current;
    if (!box) return;
    setOverflowing(box.scrollHeight > box.clientHeight + 1);
    setHiddenBelow(box.scrollHeight > box.scrollTop + box.clientHeight + 1);
  }, []);
  useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
    measure();
    // Only the rendered teaching text changes this measurement, not quote ticks.
  }, [children, measure]);
  return (
    // The quick reference appears only while the text is focusable, and on
    // hover only: a trader may stay in the box (keys act on the aimed card),
    // and a focus tooltip would sit over the board below the header.
    <Tooltip
      content={focusable ? LT2_READOUT_QUICK_REF : null}
      openOnFocus={false}
      position="bottom"
      wrapperClassName="block h-full min-w-0 flex-1"
    >
      <span
        ref={ref}
        data-testid="lt2-target-readout-text"
        // #1080: keys pressed in here act on the aimed card; the value names
        // this box in anything the hotkey listener says about it.
        data-lt2-readout="target"
        title={title}
        tabIndex={focusable ? 0 : undefined}
        role={focusable ? 'region' : undefined}
        aria-label={focusable ? 'Hotkey target (scrollable)' : undefined}
        onFocus={readout.onFocus}
        onBlur={readout.onBlur}
        onScroll={measure}
        onWheel={readout.onWheel}
        // Scroll keys stay here and Escape leaves the box with the aim kept;
        // every other key reaches the hotkey listener (#1080).
        onKeyDown={readout.onKeyDown}
        className="block h-full overflow-y-auto break-words"
      >
        {children}
      </span>
      {hiddenBelow && <span aria-hidden data-testid="lt2-target-readout-more" className="bg-background pointer-events-none absolute bottom-0 right-0 text-[9px]">more ↓</span>}
      {/* #1080: over the box's lower lines, out of flow; the target text
          underneath is untouched and returns when the line goes. */}
      {readout.line && (
        <span role="status" data-testid="lt2-readout-line" className={LT2_READOUT_LINE_CLASS}>
          {readout.line}
        </span>
      )}
    </Tooltip>
  );
}
